// Room registry — the three demo interiors (Demo Build Plan).
// Each room declares its splat manifest, a *designed spawn* (Demo DoD #2:
// aligned, eye-height, best angle — these are the defaults; a manifest may
// ship a `spawn` of its own which overrides these once the splat loads),
// and interior orbit clamps sized to the room so the camera can't leave it.
export const ROOMS = {
  // "Your Room Is The Studio" — a USER-SCANNED room (phone RoomScan → Gemini
  // restyle → Marble bake; see API scripts/room_studio_spike.js). No city
  // anchor yet: enter directly with ?enter=room-studio. The manifest+spz land
  // in public/worlds/ from the bake's --download --out.
  'room-studio': {
    name: 'My Studio',
    manifest: '/worlds/room_studio.json',
    accent: '#ffb25e',
    // Seated-capture eye height; the pano was shot at the desk, so spawn
    // matches the capture point looking toward the console wall.
    spawn: { pos: [0, 1.35, 0.4], look: [0, 1.3, -1.2] },
    orbit: { min: 0.01, max: 2000, minPolar: 0, maxPolar: 3.14 },
    // MEET panel — dormant until a live meet lands (MeetDock → liveVideoEl):
    // no recorded cameo, so nothing renders until the artist goes live on it
    // (?panel=edit shows an amber ghost for placement). FIXED angle, not
    // billboarded: this panel is placed AS A SCREEN in the room — billboarding
    // is reserved for free-standing composited characters at open positions.
    // Tune with ?panel=edit (arrows move, u/j raise/lower, [ ] rotate,
    // -/= resize, b toggles billboard) and paste the logged line over this one.
    videoPanel: { pos: [0, 1.25, -1.3], rotY: 0, height: 1.4, billboard: false },
  },
  studio: {
    name: 'The Studio',
    manifest: '/worlds/studio.json',
    accent: '#ffb25e',
    // THE DOORMAN (Khari, 2026-08-03: "we have a 'bouncer' at the concert
    // hall.. use an instance of him and do the same thing for the studio").
    // Same split as `requires`: the RULE lives here, the CHECK lives in
    // enterInterior, the VOICE lives in AccessRefusal. This one is about the
    // STORY rather than the membership ladder — you get in when Spor1 has
    // driven you and the salon has made you presentable, in that order.
    requiresStory: true,
    // Entry view (user-framed): standing at the console looking dead-on at
    // the workstation + booth-window cameo. KEEP IN SYNC with the manifest's
    // spawn (the manifest wins once the splat loads).
    spawn: { pos: [0, 1.61, 1.84], look: [0, 1.6, 1.53] },
    // User-tuned in ?cam=edit: azimuth + polar fully open (free look), distance
    // effectively unclamped; bounds clamp still does wall containment.
    orbit: { min: 0.01, max: 2000, minPolar: 0, maxPolar: 3.14 },
    // Video cameo placement: couch side of the room, angled toward spawn.
    // Don't trust guesses here — tune live with ?panel=edit (arrows move,
    // PgUp/PgDn raise, [ ] rotate, -/= resize, Shift = fine) and paste the
    // console-logged line over this one.
    // Staged BEHIND the mic-booth window (the frame mattes the panel edges
    // and the frame/panel parallax feigns booth depth). billboard OFF — a
    // room behind glass must not swivel with the viewer; works with limited
    // orbit so users can't flank it. Paste tuned numbers from ?panel=edit.
    // Derived from the "on point" screenshot: card centered right-of-middle
    // behind the booth window plane (back wall ≈ z −2.4), mid-window height,
    // tall enough that the frame crops it. Fine-tune with ?panel=edit.
    videoPanel: { pos: [0.19, 0.57, -3.13], rotY: 0.08, height: 7.44, billboard: false },
    // THE WALL OF GOLD RECORDS. Each entry is one framed plaque on the studio
    // wall, and each holds ONE of the artist's own tracks — slot 0 is their
    // newest. Clicking one is where artwork and release live, so "put my record
    // out" gets a place in the world instead of a button on a website.
    //
    // Placed like `videoPanel` above, and for the same reason: this room is a
    // Gaussian SPLAT, so there is no mesh to attach a surface to and nothing for
    // the promotions tool's ghost to raycast onto. Hand-tuned transforms are the
    // only way to hang anything on a splat wall.
    //
    // Tuned in-world against the splat (⚙ → Record wall). The frames sit on the
    // left-hand wall, fanning slightly as it angles away — hence the drifting
    // rotY rather than one shared angle. Re-tune and paste over this block.
    plaques: [
      { pos: [-2.04, 1.65, 2.14], rotY: 2, size: 0.42 },
      { pos: [-2.36, 1.65, 1.54], rotY: 1.92, size: 0.42 },
      { pos: [-2.49, 1.65, 0.9], rotY: 1.76, size: 0.42 },
      { pos: [-2.6, 1.65, 0.27], rotY: 1.68, size: 0.42 },
    ],
    // RESULT CARDS — where a finished track lands, in front of you, the moment
    // the room hands it back. Two of them because a generation run gives you
    // takes to choose between, not one answer.
    //
    // The wall behind you is what you PUT OUT; these are what just came out of
    // the room, released or not. That split is the whole point: the reveal is
    // immediate and in your eyeline, and only a RELEASED record goes on the wall.
    //
    // Tuned in-world (⚙ → Records → Results). Angled in toward the chair, so
    // both takes face whoever is sitting at the board.
    resultCards: [
      { pos: [-0.57, 1.5, 0.31], rotY: 0.25, size: 0.55 },
      { pos: [0.52, 1.5, 0.34], rotY: -0.25, size: 0.55 },
    ],
    // Live Anam video chat — same brain/billing as the web Studio page.
    // When a session goes live, the booth-window card swaps its recorded
    // cameo for the live WebRTC stream (LiveChatDock → worldStore.liveVideoEl).
    liveChat: { avatarId: 26, name: 'Rebel Rhythm' }
  },
  'listening-room': {
    name: 'The Listening Room',
    manifest: '/worlds/listening-room.json',
    accent: '#ff5e78',
    spawn: { pos: [0, 1.6, 2.8], look: [0, 1.2, -1.4] },
    // pan: right-drag / two-finger slides the pivot so you can look anywhere,
    // including behind you (bounds still keep you off the fuzzy outer shell).
    orbit: { min: 0.5, max: 9, pan: true }
  },
  'concert-hall': {
    name: 'The Concert Hall',
    manifest: '/worlds/concert-hall.json',
    accent: '#7dd3ff',
    // WHO GETS IN. A rung on the access ladder (services/access.js): 'everyone',
    // 'member', 'plan', 'body'. Absent means anyone, which is what every other
    // room in here is and should stay.
    //
    // On the ROOM rather than on the door, because there are six ways to open a
    // venue and only one of them has a guard standing next to it. The rule
    // belongs to the place; the guard is how the refusal is PERFORMED, and if
    // he is ever moved, deleted or not yet loaded the hall is still shut.
    //
    // Client-side, so this decides whether the door opens and what he says —
    // not whether the API will serve what is inside. See access.js.
    requires: 'plan',
    // firstPerson: free drag-look + WASD walk through the hall, kept inside by
    // the room bounds (and off the stage for non-admins) via CameraBounds.
    firstPerson: true,
    // Center-floor, eye height, facing the stage — mirrors where the Chisel
    // blockout places the pano camera, so splat quality is best right here.
    spawn: { pos: [0, 1.7, 7], look: [0, 2.4, -8] },
    orbit: { min: 0.5, max: 20, pan: true },
    // Interim shipped performer — a transparent-PNG cutout composited on the
    // stage. ?perfimg=<url> overrides it live; PerformerPanel tunes height/feet.
    // FUTURE: DB-driven via a `performances` table so artists can reserve + set
    // this stage slot dynamically. See reference_world_placeable_naming.
    performer: { img: '/performers/avva.png' },
  },
  theater: {
    name: 'The Cinema',
    accent: '#c8a2ff',
    // No splat — a built black-box theater: a big flat 16:9 IMAX screen on the
    // back wall playing the /listen music videos. firstPerson: drag-look + WASD,
    // kept in the room (and out of the screen wall) by CameraBounds.
    firstPerson: true,
    // Mid-house, eye height, facing the screen (screen sits on the back wall,
    // −z). A little back from center so the big screen fills the view.
    spawn: { pos: [0, 1.7, 9], look: [0, 6, -15] },
    orbit: { min: 0.5, max: 24, pan: true },
  },
  'record-store': {
    name: 'Wax & Vinyl',
    manifest: '/worlds/record-store.json',
    accent: '#9dff7d',
    // Blend: Marble splat shop + live overlay (RecordStore.jsx).
    // firstPerson: stand still and DRAG to look (rotate in place, no orbit
    // float); WASD to pan around the shop. rooms.js spawn is authoritative
    // (RecordStore does not push the manifest spawn) so ?cam=edit "copy spawn"
    // can re-aim the entry framing.
    spawn: { pos: [-0.29, 1.6, 1.53], look: [0.62, 1.55, -0.25] }, // ?cam=edit copy-spawn
    firstPerson: true,
    // CARDS — the featured sleeves hung on the splat wall. THIS ARRAY IS THE
    // SHELF SIZE: the track list is sliced to its length, so three entries show
    // three records however many the API returns. Lives here with the studio's
    // `plaques` and `resultCards` because a room's placeables belong to the room,
    // and because the ⚙ editor panel can then read them without importing the
    // 3D module. Tuned in-world (⚙ → Record sleeves).
    cards: [
      { pos: [1.55, 1.91, -1.99], rotY: -0.48, size: 0.5 },  // tuned
      { pos: [0.59, 1.91, -2.44], rotY: 0, size: 0.5 },      // tuned
      { pos: [2.38, 1.91, -1.5], rotY: -0.72, size: 0.5 },   // tuned
      { pos: [-0.37, 1.91, -1.99], rotY: 0.48, size: 0.5 },  // extrapolated
      { pos: [-1.2, 1.91, -1.5], rotY: 0.72, size: 0.5 },    // extrapolated
      { pos: [1.55, 1.29, -1.99], rotY: -0.48, size: 0.5 },  // extrapolated
      { pos: [0.59, 1.29, -2.44], rotY: 0, size: 0.5 },      // extrapolated
      { pos: [2.38, 1.29, -1.5], rotY: -0.72, size: 0.5 },   // extrapolated
    ],
    // RESIDENTS — avatars standing inside the splat. A splat has no geometry to
    // raycast, so there is nothing to drop a body onto and no floor to snap to:
    // every position here is authored by eye against the photoreal shop
    // (⚙ → Interior avatar). `slug` joins to models/avatars/roster.json.
    residents: [],
    // Tilt (pitch) limits for the first-person look — how far up/down the
    // visitor can look (radians; tune in ?cam=edit → "copy look config").
    look: { tiltMin: -0.12, tiltMax: 0.6 }, // ?cam=edit copy-look
    orbit: { min: 0.4, max: 12, pan: true }
  },

  // ── MESH VENUES ─────────────────────────────────────────────────────────
  // A room with a `model` is a GLB interior rather than a Gaussian splat, and is
  // rendered by the generic MeshInterior — no bespoke component. It loads when
  // entered and frees its GPU memory when left, which is what lets us keep
  // adding them.
  //
  //   model      the GLB (see scripts/extract_model.mjs for how these are built)
  //   fitTo      OPTIONAL longest-horizontal-span in world units. Omit when the
  //              model is already authored in metres, as both of these are.
  //   modelRotY  OPTIONAL yaw, for a model whose front faces the wrong way.
  //
  // The spawn and orbit values below are STARTING POINTS, placed at the model's
  // measured centre at eye height. Walk in with ?cam=edit and copy the tuned
  // spawn over them — every other room in this file was set that way.
  subway: {
    name: 'The Underground',
    accent: '#8fb7ff',
    model: '/models/venues/subway_station.glb',
    // Rebuilt 2026-07-28 from DOCS/WORLD/old_metro_tunnel_free.glb, the ORIGINAL
    // download — the shipped metro.glb had been joined, which merges primitives
    // by material and leaves no object names to remove anything by.
    //
    //   --strip=^node_0   THE WHOLE PROPS LAYER. This model splits cleanly in
    //     two: everything structural is named for the primitive it was built
    //     from (Plane = shell, floor, ceiling and beams; Cube.001 = the central
    //     divider; Cylinder.002 = the track bed), and every piece of clutter is
    //     `node_0.*`. The clutter was 91% OF THE TRIANGLES — 153,480 -> 12,884,
    //     10.7MB -> 1.7MB, 11 draw calls — for trash piles standing in the ring
    //     at the centre, which is not somewhere the player goes.
    //
    //     The worst single offender was node_0.014_Material.013_0: 49,980
    //     triangles inside a 1.2m box, a third of the model on its own.
    //
    //     TO BRING SOMETHING BACK (benches went with the trash — they are in
    //     here somewhere), narrow the regex and rebuild:
    //       node scripts/extract_model.mjs DOCS/WORLD/old_metro_tunnel_free.glb \
    //         - public/models/venues/metro.glb 1024 0 "--strip=^node_0\.(0|1)"
    //     Find the piece you want with scripts/mesh_bounds.mjs, which prints
    //     each object's size and position next to its name. The source download
    //     is untouched, so nothing here is a one-way door.
    //
    // ── SCALE ────────────────────────────────────────────────────────────────
    // Measured 21.8 x 2.84 x 27.7 in METRES. One world unit is ~2.2m here, so
    // the 27.7m long axis is 12.6 world units — the same conversion the corner
    // market uses. Without this it rendered at 1:1 and the station was 27.7
    // units long, two and a bit times its real size, which is what made it feel
    // like walking through a canyon.
    // 102.99m on the long axis ÷ 2.2 = 46.8 units. MeshInterior fits by
    // `fitTo / max(size.x, size.z)`, so this is the long axis and nothing else.
    //
    // Nearly FOUR TIMES the station it replaces (the old one was 27.7m / 12.6u),
    // which is the single biggest thing to have in mind when reading the numbers
    // below — every one of them was re-derived, none survived the swap.
    fitTo: 46.8,
    // ── AN ISLAND PLATFORM ───────────────────────────────────────────────────
    // This is the shape that decides where you may stand. There are track beds
    // on BOTH sides — model x −9.35..−6.45 and 6.35..9.25, sitting 1.4m below
    // the platform — with the walkable platform between them and the train
    // standing on the +x track. So the room is a long central island with a drop
    // either side, not a corridor with walls.
    //
    // ── READ OFF THE PLAN, NOT DERIVED FROM THE BOUNDS ───────────────────────
    // The first version of this put x at 0 because that is the middle of the
    // model, and the middle of the model is a row of BENCHES AND COLUMNS —
    // scripts/xz_map.mjs at walker height (0.6..1.8) shows geometry at model
    // x −0.7..+0.75 down the whole length, and the train filling x +5.0..+10.5.
    // Open platform is what is left: roughly x −6.4..−0.7 on the west side.
    //
    // So this stands at model x −3.5 (÷2.2 = −1.59), mid-way along the west
    // platform with the benches to your right and the train beyond them.
    //
    // STILL PROVISIONAL. Fly with F (Q/E), stand where it should be, and run
    // world.where() — it prints this line ready to paste. That is the only way
    // this gets right; deriving it from a bounding box is what put the last one
    // inside the furniture.
    // Stood at the bottom of the stairs and read off ⚙ → cam. The stairs are the
    // arrival point for everything: you come through the street portal and are
    // simply THERE, and the exit portal to the next city sits in the same place.
    // They are never climbed, so the station stays one floor.
    spawn: { pos: [-0.11, 1.13, -13.94], look: [-0.07, 1.28, -11.95] },
    // 46.8 units end to end — by far the longest interior in the world, and the
    // one that exposed interiors walking at 2.4 u/s against the street's 9. At
    // 6 it takes ~17s to pace the platform, which is a walk rather than a
    // commute. Tune live with ?walkspeed= and bake the number back here.
    walkSpeed: 6,
    // NO `walk` BLOCK, deliberately, and it is not an omission.
    //
    // MeshInterior calls setRoomBounds(null) for every mesh room — the floor is
    // the boundary, answered by FloorClamp's raycast, which is exact for a shape
    // no box can describe. A `walk` box here is read by nobody: it is dead
    // config that LOOKS live, and the camera panel correctly reports "bounds:
    // none" while it sits in the file, which is how somebody loses an afternoon
    // tuning numbers that do nothing.
    //
    // Splat rooms still need one. They have no geometry to raycast against.
    firstPerson: true,
    look: { tiltMin: -0.3, tiltMax: 0.5 },
    orbit: { min: 0.4, max: 40, pan: true },
  },
  salon: {
    name: 'The Salon',
    accent: '#f0a6c0',
    // The room has its own sound — gossip on the floor, the news on the TV — so
    // the district station fades out on the way in and back on the way out
    // (RoomSound + audioEngine.roomQuiet). Only ever RESTORED, never imposed:
    // walk in with the music already off and it stays off when you leave.
    ownSound: true,
    model: '/models/venues/salon.glb',
    // ── THE FIRST ROOM WE BUILT RATHER THAN FOUND ───────────────────────────
    //
    //   node scripts/make_salon.mjs     ← regenerates the GLB in ~1 second
    //
    // Source of truth is scripts/make_salon.mjs, not the .glb. To move a chair,
    // widen the room or add a fifth station, edit the descriptor at the top of
    // that file and re-run it. The binary is build output.
    //
    // NOTE WHAT IS MISSING FROM THIS ENTRY, because it is the entire argument
    // for authoring:
    //
    //   no fitTo      — authored in metres and converted at 1u ≈ 2.2m on the way
    //                   out (scripts/lib/kit.mjs). The metro needed 12.6 and the
    //                   market 13.4, each derived by measuring props inside the
    //                   model and reasoning back to a real-world size, and each
    //                   invalidating that room's spawn when it changed.
    //   no yNudge     — the floor's top surface is at y=0 BY CONSTRUCTION, so
    //                   detectFloor has nothing to infer.
    //   no --strip    — nothing is in here that nobody put here.
    //   no modelRotY  — it was authored facing the way it faces.
    //
    // 58,944 triangles, 2.1MB, 61 draw calls, NO TEXTURES — the whole look is
    // geometry and a 23-colour palette. 611 loose parts (bulbs, bottles, towels,
    // mouldings) merge by material at build time; the ~49 objects carrying
    // affordances are kept whole, which is the one thing merging must not eat.
    // For comparison the subway entrance arrived at 125MB.
    //
    // WHAT MAKES IT READ AS BOUGHT rather than blocked out, since the first pass
    // of this room was four chairs and two sinks in a box:
    //   architecture   dropped soffit over the styling run, arch on pilasters
    //                  framing the wash alcove, coffered ceiling, cornice,
    //                  skirting, panelled wainscot, inlaid floor border
    //   edges          every manufactured object is a rounded box, a lathe or an
    //                  extruded profile, so a specular line runs down each edge.
    //                  A 90° edge returns one flat value and reads as a blockout
    //                  no matter how well proportioned it is.
    //   dressing       bottles, jars, towels, brushes, hairdryers, a trolley, a
    //                  broom, magazines, plants, a coat stand. A salon with
    //                  nothing left out is a showroom of salon furniture.
    //
    // AFFORDANCES COME FROM THE BUILDER. Seats, surfaces, mirrors, shelves and
    // the two postable walls carry `userData` (written as glTF node extras, read
    // back by GLTFLoader) naming what they are and which way they face — because
    // the code that placed them knew, and a mesh alone never can. Read them off
    // any child: `child.userData.affordance`.
    //   seat      12 of them — styling chairs, wash chairs, hood dryers, stools,
    //             the sofa — each with `facing` and `seatHeight`
    //   surface   counter tops, colour bar, reception, trolley, side table
    //   mirror    the four station mirrors
    //   shelf     retail, four levels
    //   lamp      soffit cove, coffer downlights
    //   postable  accent wall, retail panel, the sign over reception
    //   album_slot  12 RECORD SLEEVES racked in the retail case, at and above
    //             eye height. Each is its own mesh — the merge pass keeps
    //             affordance-bearing meshes whole precisely so there is a real
    //             surface here to swap artwork onto. Authored at 314mm square,
    //             the real sleeve size, so cover art lands at the right
    //             proportion with nothing to tune. `facing` and `size` are on
    //             each one; which shelves rack albums is `retail.albums` in the
    //             generator, so a fifth shelf of them is a one-line change.
    //
    // Lighting: pale walls and a lot of white stone bounce plenty, so the rig
    // runs under 1. Ceiling is at 1.55u, hence the practicals just below it.
    light: 0.9,
    lampHeight: 1.4,
    // RESIDENTS — `slug` joins to models/avatars/roster.json.
    //
    // Unlike the splat rooms, none of these were authored by eye: the room came
    // out of a descriptor, so the furniture coordinates are KNOWN and every
    // position below is derived from it, divided by 2.2 on the way out because
    // the descriptor is in metres and the world is not.
    //
    // Sassy La starts BEHIND THE RECEPTION DESK — x 3.6m (SALON.reception.x), in
    // the ~1.1m gap between the desk's back edge (z 3.42m) and the back bar
    // against the +z wall (z 4.58m), turned to face into the room. That is the
    // staff side: the desk's curved front bulges toward −z, into the customer's
    // half. It also puts her in shot on arrival, since the spawn looks diagonally
    // across her.
    //
    // Overridden the moment anything is saved for this room in world_room_objects
    // (⚙ → Interior avatar), which is what to nudge with rather than this line.
    residents: [
      { slug: 'sassy_la', pos: [1.64, 0, 1.82], rotY: 3.14, height: 0.75 },
    ],
    // STAGE MARKS — the places a character can be SENT, by name.
    //
    // The room owns these and the scene owns the cue, so a scene says "go to the
    // chair" and never learns a coordinate. Move the wash alcove in the
    // descriptor and this is the one line that follows it; the scene does not
    // change at all.
    //
    // `chair` is a metre to the +x side of wash chair 1 (x −6.2m, z −3.65m, i.e.
    // SALON.basins[0] and cz = −d/2 + 1.35), turned to face it. Beside the chair,
    // not on top of it — she is working on someone in it.
    //
    // `home` is omitted deliberately: home is wherever the resident was PLACED,
    // so a position nudged live in ⚙ → Interior avatar is still the place she
    // walks back to. Duplicating it here would mean she returns to a coordinate
    // that has not been true since the last time anyone tuned the room.
    stage: {
      sassy_la: {
        // x −2.36 → −2.13: half a metre FURTHER from the chair (Khari,
        // 2026-08-03 — on his double-wide monitor the wider frame crops
        // tighter vertically and her model clipped the camera at 1m; ~1.5m
        // keeps her in frame at every aspect). Serving distance, not spawn:
        // spawn is the resident placement above (⚙-tunable); THIS mark is
        // file-authored only.
        chair: { pos: [-2.13, 0, -1.66], rotY: -1.57 },
      },
    },
    // AND THE CUE. Sitting down in a salon is a request for service, so the
    // stylist comes over — after a beat, because arriving the instant you land
    // reads as a trigger firing rather than as someone noticing you.
    //
    // Deliberately not in `stage`: the marks are geography and outlive any
    // particular scene, while this is a beat somebody wrote and is the thing
    // most likely to be replaced by the dialogue director naming its own cues.
    // One cue per room for now — enough for the salon, and honest about it.
    onSit: { slug: 'sassy_la', mark: 'chair', delayMs: 3200 },
    // Just inside the entrance (x +2.73, z +2.27), turned to take the room in
    // diagonally: the styling run and its mirrors down the left wall, the arch
    // and wash alcove beyond them, reception forward-left, waiting on your right.
    spawn: { pos: [2.73, 0.75, 1.85], look: [-0.5, 0.68, -0.5] },
    firstPerson: true,
    look: { tiltMin: -0.3, tiltMax: 0.5 },
    orbit: { min: 0.4, max: 40, pan: true },
    // No `walk` block: FloorClamp raycasts down onto the floor and the walkable
    // area is the floor's real outline. It has no opinion about walls, so you
    // can still step up onto a counter — the same limitation every mesh room
    // has, and the reason this room's furniture is authored at real heights.
  },
  grocery: {
    name: 'Corner Market',
    accent: '#ffd27d',
    // Tuned in-world. Multiplies MeshInterior's whole rig — hemisphere fill,
    // ambient and ceiling practicals together — so the balance holds and this
    // only moves exposure. Below 1 because the shop's own white fixtures and
    // pale floor already bounce a lot.
    light: 0.79,
    // SITED: a public place belongs to the city. The shop stands at its venue
    // anchor, at that building's coordinates, and its windows look out on the
    // real street — the city keeps rendering while you are inside, culled to
    // SITED_CULL (70u) so you get the facing block and nothing behind it.
    //
    // The private rooms stay virtual: an apartment is instanced at the origin
    // with the world hidden, because there is no one place in the city it "is".
    placement: 'sited',
    // WHICH BUILDING IT STANDS ON. Uncomment with a mesh name to pin it there;
    // leave it off and the sampler scatters this venue with the others, which is
    // fine for a first look and wrong the moment you care about the view out.
    //
    // Find the name in-world: ?pick=1, then shift-click the building — the HUD
    // reads back what you hit. A NAME rather than coordinates because the city
    // is auto-fitted, so every coordinate in it moves if the model is re-exported
    // or the fit changes, while the name survives.
    // anchorMesh: 'LM_Liquor',
    // Inert while `placement` is 'sited' (PlanetScene: showBackdrop = !sited &&
    // …). Kept so dropping the line above falls back to the sealed-with-a-
    // horizon look rather than to black, which is what the windows show with
    // neither set.
    exterior: 'backdrop',
    // Which way the shopfront faces once it is standing in the city. The model's
    // own orientation has no reason to match the street it was anchored to, and
    // geometry cannot say which wall is the front. Tune live with ?venuerot=
    // (degrees) and bake the number here.
    modelRotY: 0,
    // Just under the ceiling, which fitTo now puts at 2.0 world units (the
    // model's real 4.42m). Was 5.2, tuned when the model measured 33.6 tall
    // because of the floating stock — that hung the practicals two and a half
    // times higher than the roof they are supposed to be mounted in.
    lampHeight: 1.8,
    //timeOfDay: 'midday',
    model: '/models/venues/supermarket.glb',
    // THE OUTSIDE, standing in the city all the time. Extracted from the same
    // source as the interior (--shell), so the two line up by construction
    // rather than by tuning. Without it a sited venue is an invisible hole in
    // the street and VenueShells falls back to a massing block.
    shell: '/models/venues/supermarket_shell.glb',
    // ── SHELL ALIGNMENT — set these by eye, they are meant to be ────────────
    //
    // The shell auto-fits and auto-grounds exactly as the interior does, and it
    // is usually right. When it is not, these two win outright rather than being
    // one more input to an inference. Two numbers you can see the effect of beat
    // a heuristic that is correct most of the time and unexplainable when it is
    // not — the shell and the interior are separate models fitted by separate
    // code, and closing the last few centimetres between them by hand is faster
    // and more honest than another rule.
    //
    //   shellFit   the shell's longest horizontal span, in world units. Leave
    //              unset to inherit the interior's measured footprint. Set it if
    //              the shell reads bigger or smaller than the room inside it.
    //   shellY     lifts (+) or sinks (−) the shell only, in world units, after
    //              grounding. The venue tool's Y moves BOTH; this is the trim
    //              between them.
    //
    // The console prints `[shell] floor -> …` and `[venue] grocery: …` on entry;
    // read them together and the gap is the number to type here.
    // shellFit: 13.4,
    // shellY: 0,
    // Rebuilt 2026-07-27 from DOCS/WORLD/models/corner_store, which is the
    // ORIGINAL download — 3,483 separate meshes, where our previous build had
    // been joined to 27 and had lost the object structure that both of these
    // passes need:
    //
    //   --keep-y=:8   140 product meshes were floating at y 22-29, sixteen units
    //                 above the roof, with nothing in between. They contributed
    //                 6k triangles and, far worse, made the model measure 33.6
    //                 TALL — so every automatic fit here sized a room, a light
    //                 rig and a camera against a building six times its real
    //                 height. That is what made the shop feel doll-sized.
    //                 Now 29.5 x 10.2 x 18.1, which is a supermarket.
    //   --thin=0.3    2,325 duplicate products removed across 36 groups, at
    //                 least one of every product kept. 388k -> 210k triangles.
    //                 Simplification was the wrong lever: a 20-triangle cereal
    //                 box cannot lose half its triangles and still be a box.
    //
    // Aim a re-clip with scripts/y_profile.mjs, which prints where the geometry
    // actually sits — a bounding box cannot tell a basement from a roof.
    //
    // ── SCALE, DERIVED RATHER THAN EYEBALLED ────────────────────────────────
    //
    // THE SET SCALES, NOT THE PLAYER. Your eye height is a world constant —
    // shared by the street, the residents, multiplayer peers, walk speed and the
    // camera's near plane — so changing it per room means being a different size
    // when you walk out and peers rendering wrong beside you. A downloaded
    // model's scale, by contrast, is simply WRONG until corrected, and wrong the
    // same way in every zone and story instance that ever uses it. Correct it
    // once, here, and nothing downstream has to compensate.
    //
    // The number is measured, not guessed. This model is authored in REAL
    // METRES — confirmed by three independent references in it: the `свет`
    // ceiling fixtures sit at 4.42, the `белый` shelving bays are 2.2 tall, and
    // the `стекло холод` fridges are 2.2. Those are correct supermarket
    // dimensions, so the model's unit IS a metre.
    //
    // The world's unit is not. A 0.75 eye height standing in for a ~1.65m human
    // eye makes ONE WORLD UNIT ≈ 2.2 METRES, so a metric model converts at
    // 1/2.2 = 0.4545. Across the 29.5m shop floor that is 13.4 world units.
    //
    // Checks out both ways: shelving lands at 1.0 units against a 0.75 eye
    // (1.33x, exactly 2.2m against 1.65m), and the ceiling at 2.0 (2.7x, exactly
    // 4.42m against 1.65m). You stand in it at the height you stand in a shop.
    //
    // NOT fitted to the bounding box, deliberately — that box includes roof
    // structure above the ceiling you actually walk under, which is what made
    // every earlier attempt oversize the room around the player.
    fitTo: 13.4,
    // Eye height 0.75 — the WORLD's human scale (PlanetScene's EYE), not the
    // 1.6 these rooms were authored with. 1.6 is a person in metres, and this
    // world is not in metres: it made you 2.1x taller indoors than you were on
    // the pavement a second earlier, which is why a shop tuned to look right
    // still felt built for a child.
    //
    // z 3, not 8. The old value was authored against the model's raw 18.1m
    // depth; at fitTo the shop is 8.2 units deep, so z 8 stood you outside the
    // back wall. Room-local spawns are in WORLD units and do not scale with
    // fitTo, so this has to move whenever the fit does.
    spawn: { pos: [0, 0.75, 3], look: [0, 0.7, -3] },
    firstPerson: true,
    look: { tiltMin: -0.3, tiltMax: 0.5 },
    orbit: { min: 0.4, max: 40, pan: true },
  },
};

// ── The town block ────────────────────────────────────────────────────────
// The four locations are spread across a deep block (not a row): two face the
// main street, two sit back — one down an alley, the concert hall at the far
// corner. Each has a `world` footprint (where its building stands, facades on
// −z), a `street` camera pose (where you "arrive" when you pick it on the map),
// and a `map` coord (0..1, for the HUD minimap; x = left→right, y = far→near).
// Camera approaches from −z, so a smaller z is nearer the viewer.
export const STREET_OVERVIEW = { pos: [0, 8, -14], look: [1, 3, 18] };

export const TOWN_LOCATIONS = [
  {
    key: 'studio', name: 'The Studio', accent: '#ffb25e', interiorId: 'studio',
    world: [-22, 12], map: [0.16, 0.74],
    street: { pos: [-22, 3.2, 3], look: [-22, 2.9, 12] },
  },
  {
    key: 'listening-room', name: 'Listening Room', accent: '#ff5e78', interiorId: 'listening-room',
    world: [-6, 27], map: [0.42, 0.30], // down the left alley, set deep
    street: { pos: [-6, 2.8, 15], look: [-6, 2.6, 27] },
  },
  {
    key: 'record-store', name: 'Record Store', accent: '#9dff7d', interiorId: 'record-store',
    world: [20, 12], map: [0.78, 0.74],
    street: { pos: [20, 3.2, 3], look: [20, 2.9, 12] },
  },
  {
    key: 'concert-hall', name: 'Concert Hall', accent: '#7dd3ff', interiorId: 'concert-hall',
    world: [22, 31], map: [0.84, 0.22], // far corner, biggest venue
    street: { pos: [22, 4.5, 19], look: [22, 3.6, 31] },
  },
  {
    key: 'theater', name: 'The Cinema', accent: '#c8a2ff', interiorId: 'theater',
    world: [4, 40], map: [0.52, 0.08], // deepest back-center of the block
    street: { pos: [4, 3.2, 28], look: [4, 2.9, 40] },
  },
  // The two mesh venues. Placed on the OPPOSITE side of the main street from
  // the music venues, so the block reads as a neighbourhood with everyday
  // places in it rather than a row of five studios. These world coords are a
  // first pass — the real placement is whichever buildings you anchor them to.
  {
    key: 'grocery', name: 'Corner Market', accent: '#ffd27d', interiorId: 'grocery',
    world: [-38, 24], map: [0.06, 0.36],
    street: { pos: [-38, 3.2, 14], look: [-38, 2.9, 24] },
  },
  {
    key: 'salon', name: 'The Salon', accent: '#f0a6c0', interiorId: 'salon',
    world: [-30, 6], map: [0.16, 0.68], // everyday side, between market and street
    street: { pos: [-30, 3.2, -4], look: [-30, 2.9, 6] },
  },
  {
    key: 'subway', name: 'The Underground', accent: '#8fb7ff', interiorId: 'subway',
    world: [10, -8], map: [0.60, 0.92], // near the front of the block — a street entrance
    street: { pos: [10, 3.2, -18], look: [10, 2.9, -8] },
  },
];

export const streetPoseFor = (key) => TOWN_LOCATIONS.find((l) => l.key === key)?.street || null;

// ── YOU GO IN THROUGH THE DOOR ───────────────────────────────────────────────
// A venue is entered through a PLACED PORTAL — a trigger volume somebody put in
// front of the entrance with the admin tool — not by clicking the building.
//
// The building-as-hit-zone came first because it always works: no placement, no
// tuning, click the massing block and you are inside. What it costs is the
// premise. A building you click is a menu item wearing a facade, it has no
// front, and it cannot be guarded — the doorman is standing at an entrance that
// is one of a thousand equally valid ways in, which is exactly the problem the
// concert hall's guard exposed.
//
// WHAT THIS DOES NOT TOUCH: the venue markers and the map. Both are wayfinding
// UI, presented as UI, and both still enter. The thing being retired is the
// pretence that a BUILDING is a button — clickable through a wall, on the roof,
// or from across the block, which is what leaves a venue with no front and
// nothing that can be stationed at it.
//
// So a venue with no portal is not stranded; it is only unreachable by walking
// up to it, which is the mode the whole change is about. Portals still warns by
// name about every venue missing a door, because "you cannot walk in" is not
// obvious from a building that silently ignores you.
//
// Every route in — door, marker, map, fast travel — goes through the same
// access gate in enterInterior, so none of this is a way past a guard.
//
// ?hitzone=1 restores the old clicks for a session, for placing the first door
// in a world that has none.
export const ENTRY_VIA_PORTAL = typeof window === 'undefined'
  || new URLSearchParams(window.location.search).get('hitzone') == null;
